import { AccountTree, Delete, Edit, MoreVert } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import MenuConfigStandard from "../../../components/MenuStyled/MenuConfigStandard";
import { ValidationRuleVORetrieve } from "../../../model/ValidationRuleModel";
import { GetVrByWorkspaceId, GetVrByWorkspaceIdName } from "../../../services/ValidationRuleService";
import { ValidationRuleModalDeleteComp } from "../ModalDeleteValidationRule/ValidationRuleModalDeleteComp";
import { EmptyContainer } from "../../../components/KeydriveComponents/EmptyContainer/EmptyContainer";

function useVisibility(
  cb: (isVisible: boolean) => void,
  deps: React.DependencyList
): (node: any) => void {
  const intersectionObserver = useRef<IntersectionObserver | null>(null);
  return useCallback((node) => {
    if (intersectionObserver.current) {
      intersectionObserver.current.disconnect();
    }

    intersectionObserver.current = new IntersectionObserver(([entry]) => {
      cb(entry.isIntersecting);
    });

    if (node) intersectionObserver.current.observe(node);
  }, deps);
}

interface ValidationRuleListCompParams {
  nameOrg: string;
  idWorkspace: string;
  text: string;
  onClickExcluir: React.MouseEventHandler<HTMLElement> | undefined;
  toggleDelete: (value: string | undefined) => void;
  idVrDelete: string | undefined;
  setSelectedItemId: (set: string) => void;
  selectedItemId: string;
}

const ValidationRuleListComp: React.FC<ValidationRuleListCompParams> = ({
  nameOrg,
  idWorkspace,
  text,
  onClickExcluir,
  toggleDelete,
  idVrDelete,
  setSelectedItemId,
  selectedItemId
}) => {
  const { t } = useTranslation();
  const [vrList, setVrList] = useState<ValidationRuleVORetrieve[]>([]);
  const [vrkMax, setVrkMax] = useState<boolean>(true);
  const [limit, setLimit] = useState<number>(50);
  const [skip, setSkip] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id?: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedItemId(id!);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const lastVr = useVisibility(
    (visible) => {
      if (visible && vrkMax) {
        if (text === "") {
          GetVrByWorkspaceId(idWorkspace, skip, limit)
            .then((newWork) => {
              if (newWork.length < limit) {
                setVrkMax(false);
              }
              setSkip(skip + newWork.length);
              setVrList([...vrList, ...newWork]);
            });
        } else {
          GetVrByWorkspaceIdName(idWorkspace, skip, limit, text)
            .then((newWork) => {
              if (newWork.length < limit) {
                setVrkMax(false);
              }
              setSkip(skip + newWork.length);
              setVrList([...vrList, ...newWork]);
            });
        }
      }
    },
    [skip, text, limit]
  );

  useEffect(() => {
    setSkip(0);
    setVrkMax(true);

    if (text === "") {
      GetVrByWorkspaceId(idWorkspace, 0, limit)
        .then((newWork) => {
          if (newWork.length < limit) {
            setVrkMax(false);
          }
          setSkip(newWork.length);
          setVrList(newWork);
        });
    } else {
      GetVrByWorkspaceIdName(idWorkspace, 0, limit, text)
        .then((newWork) => {
          if (newWork.length < limit) {
            setVrkMax(false);
          }
          setSkip(newWork.length);
          setVrList(newWork);
        });
    }
  }, [idWorkspace, text, limit]);

  return (
    <>
      {(vrList.length == 0) &&
        <Box sx={{display: "flex", flexGrow: 1}}>
          <EmptyContainer
            icon={<AccountTree fontSize='large' sx={{ color: "#22638D" }} />}
            title={t("ValidationRule.NoValidationRule")}
            content={t("ValidationRule.NoValidationRuleText")} />
        </Box>
      }
      {(vrList.length > 0) &&
        <TableContainer component={Paper} elevation={0} sx={{
          bgcolor: 'background.paper',
          borderRadius: 0,
          border: '1px solid #e0e0e0'
        }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell width={50}></TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#666', borderBottom: '1px solid #e0e0e0' }}>
                  {t("Nome.validation")}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#666', borderBottom: '1px solid #e0e0e0' }}>
                  {t("Description.message")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vrList.map((vr, index) => (
                <TableRow
                  key={vr.id}
                  sx={{
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <TableCell sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
                    <IconButton
                      aria-label="more"
                      onClick={(e) => handleMenuOpen(e, vr.id)}
                      size="small"
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ py: 2, borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant="body1" sx={{ color: '#333' }}>
                      {vr.name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 2, borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {vr.description}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <MenuConfigStandard
            anchorEl={anchorEl!}
            handleMenuClose={handleMenuClose}
            listMenu={[
              {
                icon: <Edit />,
                method: () => navigate(`/${nameOrg}/${idWorkspace}/validationRule/${selectedItemId}`),
                text: "Editar informações"
              },
              {
                icon: <Delete />,
                method: () => toggleDelete(idVrDelete!),
                text: "Deletar regra de validação"
              }
            ]}
          />

        </TableContainer>}
    </>
  );
};

export default ValidationRuleListComp;
